import { useCallback } from 'react';

import { ClaimContext, ClaimType, OnChangeClaimContext } from '../types';
import { useFileImport } from './useFileImport';
import container from '../../../../container';
import { InvoiceSource } from './useInvoiceExtraction';

export interface UseImportDocumentsProps {
  shopId: number;
  onChange: OnChangeClaimContext;
  extractInvoiceData: (fileId: number, invoiceSource?: InvoiceSource) => void;
}

export function useImportDocuments(props: UseImportDocumentsProps) {
  const { shopId, onChange, extractInvoiceData } = props;

  const [importInvoice, importInvoiceState] = useFileImport({
    fileKey: 'invoiceDocuments',
    onImportFile: ({ trackingCode, shopOrderId }: { trackingCode: string; shopOrderId: string | null | undefined }) =>
      container.touchpointsApi.postClaimsImportInvoice(shopId, { trackingCode, shopOrderId }),
    shopId,
    onChange,
    onSuccess: (file) => extractInvoiceData(file.id, 'import'),
  });

  const [importDOR, importDORState] = useFileImport({
    fileKey: 'declarationOfRecipient',
    onImportFile: (shipmentId: number) => container.touchpointsApi.postClaimsImportDOR(shopId, { shipmentId }),
    shopId,
    onChange,
  });

  const importInvoiceIfRequired = useCallback(
    (context: ClaimContext) => {
      const {
        trackingCode,
        invoiceDocuments,
        shopOrderId,
        shopSettings: { isInvoiceImportEnabled },
      } = context;

      if (isInvoiceImportEnabled && trackingCode && invoiceDocuments.length === 0) {
        importInvoice({ trackingCode, shopOrderId });
      }
    },
    [importInvoice]
  );

  const importDORIfRequired = useCallback(
    (context: ClaimContext) => {
      const {
        shipmentId,
        declarationOfRecipient,
        shopSettings: { isDORSigningLinkEnabled },
      } = context;

      if (
        isDORSigningLinkEnabled &&
        context.type === ClaimType.INVESTIGATION &&
        context.claimRequirements?.declarationOfRecipient !== 'none' &&
        shipmentId &&
        declarationOfRecipient.length === 0
      ) {
        importDOR(shipmentId);
      }
    },
    [importDOR]
  );

  const importDocuments = useCallback(
    (claim: ClaimContext) => {
      importInvoiceIfRequired(claim);
      importDORIfRequired(claim);
    },
    [importInvoiceIfRequired, importDORIfRequired]
  );

  return { importDocuments, importDORState, importInvoiceState };
}
